import React, { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import * as yup from 'yup'
import { Formik, Form, FormikProps } from 'formik'
import styled from 'particles/themes/styled'
import { useTranslation } from 'react-i18next'
import {
    selectIsSupportMassApprovalTableStepHelper,
    selectSelectedCampaignSummary,
    selectSidebarStep,
    selectSidebarTask,
} from 'state/review'

import { isSupportAddPostTimeStepType } from 'components/utils'
import { convertToBackendStr, parseBackendDate } from 'services/dates'
import { HandleMassSubmissionFunction, HandleMassSubmissionPayload } from '../types'
import { POST_TIME_WINDOW_SCHEMA } from '../../validationSchema'
import _ from 'lodash'
import RangeInput from 'particles/RangeInput'
import Modal from 'particles/Modal'
import { FormikTextArea } from 'particles/TextInputs/TextArea'

interface BasicFormProps {
    selectedNum: number
    handleMassApprove?: HandleMassSubmissionFunction
    setOpen: (isOpen: boolean) => void
    className?: string
}
interface PostTimeProps {
    start: string | null
    end: string | null
}
interface FormValues {
    comment: string
    post_time_window?: PostTimeProps | null
}
const MassApproveFormModal: React.FC<BasicFormProps> = ({ selectedNum, handleMassApprove, setOpen, className }) => {
    const [t] = useTranslation()
    const formikRef = useRef<FormikProps<FormValues>>(null)

    const sidebarStep = useSelector(selectSidebarStep)
    const sidebarTask = useSelector(selectSidebarTask)
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const isSupportMassApprovalTableStep = useSelector(selectIsSupportMassApprovalTableStepHelper)
    const schedulePostEnabled = useMemo(
        () =>
            Boolean(
                sidebarTask &&
                    sidebarTask.data &&
                    sidebarTask.data.add_post_time &&
                    sidebarStep &&
                    isSupportAddPostTimeStepType(sidebarStep.type),
            ),
        [sidebarTask, sidebarStep],
    )

    const validationSchema = useMemo(() => {
        const baseValidation = yup.object({
            comment: yup
                .string()
                .nullable()
                .max(10000, ({ max }) =>
                    t(
                        'errorMessage.validation.enterLessThanCharacterLimit',

                        {
                            max,
                        },
                    ),
                )
                .required(t('errorMessage.validation.enterComment')),
        })

        if (!schedulePostEnabled || !campaignSummary) {
            return baseValidation
        }

        return baseValidation.concat(
            yup.object({
                post_time_window: POST_TIME_WINDOW_SCHEMA(sidebarTask, campaignSummary.country),
            }),
        )
    }, [schedulePostEnabled, sidebarTask])

    const initialValues = useMemo(() => {
        if (!schedulePostEnabled) {
            return { comment: '' }
        }
        const post_time_window = { start: null, end: null } as PostTimeProps
        return {
            comment: '',
            post_time_window,
        }
    }, [schedulePostEnabled])

    const onDateChange = (handleChange: any) => (date: Date | null) => {
        handleChange(
            date && campaignSummary ? parseBackendDate(date.toISOString(), undefined, { showTime: true }) : null,
        )
    }
    const getPostTimeErrros = (errors: any) => {
        if (errors?.start) {
            return errors.start
        }
        if (errors?.end) {
            return errors.end
        } else {
            return ''
        }
    }

    const handleConfirm = () => {
        if (formikRef.current) {
            formikRef.current.submitForm()
        }
    }
    return (
        <Modal
            title={t('modalWindow.confirmationMessage.approvePartipostersBulkTitle', {
                selectedNum,
            })}
            subtext={
                sidebarStep && isSupportMassApprovalTableStep(sidebarStep.type, schedulePostEnabled)
                    ? t('modalWindow.confirmationMessage.turnOffMultiSelectSubtitle')
                    : t('modalWindow.confirmationMessage.approvePartipostersBulkSubtitle', {
                          count: selectedNum,
                      })
            }
            hasIcon={false}
            size="m"
            primaryButtonProps={{
                label: t('button.approveAll'),
            }}
            secondaryButtonProps={{
                label: t('button.cancel'),
            }}
            onConfirm={() => {
                handleConfirm()
            }}
            onClose={() => setOpen(false)}
            className={className}
        >
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    const payload: HandleMassSubmissionPayload = { comment: values.comment }
                    if (
                        campaignSummary &&
                        values.post_time_window &&
                        values.post_time_window.start &&
                        values.post_time_window.end
                    ) {
                        payload.post_time_window = {
                            start: convertToBackendStr(values.post_time_window.start, campaignSummary.country),
                            end: convertToBackendStr(values.post_time_window.end, campaignSummary.country),
                        }
                    }
                    handleMassApprove?.(payload)
                    actions.setSubmitting(false)
                    setOpen(false)
                }}
            >
                {(formikBag: FormikProps<FormValues>) => (
                    <Form>
                        {schedulePostEnabled && (
                            <RangeInput.Date
                                inputWidth="100%"
                                label={t('submission.review.schedulePostHeader')}
                                subtext={t('submission.review.choosePublishScheduleSubtext')}
                                firstRangePlaceholder={t('placeholderText.enterDateAndTime')}
                                secondRangePlaceholder={t('placeholderText.enterDateAndTime')}
                                firstRangeName="post_time_window.start"
                                secondRangeName="post_time_window.end"
                                firstRangeOnChange={onDateChange(formikBag.handleChange('post_time_window.start'))}
                                secondRangeOnChange={onDateChange(formikBag.handleChange('post_time_window.end'))}
                                firstRangeDate={
                                    formikBag.values.post_time_window
                                        ? formikBag.values.post_time_window.start || ''
                                        : ''
                                }
                                secondRangeDate={
                                    formikBag.values.post_time_window ? formikBag.values.post_time_window.end || '' : ''
                                }
                                size="m"
                                showTime
                                errorMessage={getPostTimeErrros(formikBag.errors.post_time_window)}
                                firstRangePopperPlacement="bottom"
                                secondRangePopperPlacement="bottom"
                                isFormikComponent
                            />
                        )}
                        <FormikTextArea
                            label={t('submission.review.commentsForPartiposterHeader')}
                            subtext={t('submission.review.commentsForPartiposterSubtext')}
                            minTextAreaHeight={80}
                            maxTextAreaHeight={200}
                            name="comment"
                            placeholder={t('placeholderText.enterCommentForPartiposter')}
                        />
                    </Form>
                )}
            </Formik>
        </Modal>
    )
}

export default styled(MassApproveFormModal)`
    .modal-content {
        width: fit-content;
        height: fit-content;
        overflow: visible;
        .modal-body {
            overflow-y: visible;
        }
    }
    form {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
    }
`
